import { type ReactNode, useState } from "react";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import t from "@/lang/lang";
import {
  GfsAreaAssessment,
  GfsAssessment,
} from "@/services/backend/gfs/assessment/types";
import {
  formatGfsConsumableDisplayName,
  GfsCatalogItem,
  GfsConsumable,
} from "@/services/backend/gfs/catalog/types";
import { PestcontrolMonitorType } from "@/services/backend/pestcontrol/types/monitor";
import { MonitorSelectSearchComponent } from "@/routes/processes/[processId]/qpm/_components/monitor-select-search-component";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import {
  FloatNumberInput,
  IntNumberInput,
} from "@/shared/components/domain/qpm/inputs/number-input";
import { GfsAreaType } from "@/services/backend/gfs/assessment/components/gfs-site-selector-components";
import {
  GfsPriceUnitOptions,
  normalizeGfsUnit,
} from "@/services/backend/gfs/unit";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/shared/components/ui/command";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { DialogClose, DialogFooter } from "@/shared/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TimeNumberInput } from "@/shared/components/inputs/time-number-input";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import { TextareaDebounced } from "@/shared/components/inputs/textarea-debounced";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { PersistenceStateInfo } from "@/shared/lib/persistence-state/persistence-state-info";
import { AssessmentField } from "@/services/backend/gfs/assessment/components/assessment-field";
import { DialogTooltipButton } from "@/shared/components/dialog/dialog-tooltip-button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { ScrollArea } from "@/shared/components/ui/scroll-area";

export function AreaAssessments({
  buildings,
  scope,
  areaAssessments,
  onChange,
  persistenceStates,
  catalogItems,
  catalogConsumables,
  onMonitorSelected,
  onRemove,
}: {
  buildings: GfsAssessment["buildings"];
  scope: { buildingId: string; levelId: string; areaType: GfsAreaType };
  areaAssessments: GfsAreaAssessment[];
  onChange: (next: GfsAreaAssessment[], areaId: string) => void;
  persistenceStates: Record<string, PersistenceState>;
  catalogItems: GfsCatalogItem[];
  catalogConsumables: GfsConsumable[];
  onMonitorSelected?: (item: GfsCatalogItem) => void;
  onRemove: (areaId: string) => void;
}) {
  const selected = buildings
    .filter((building) => building.id === scope.buildingId)
    .flatMap((building) =>
      building.areas
        .filter(
          (area) =>
            (area.levelId ?? "") === scope.levelId &&
            area.type === scope.areaType &&
            areaAssessments.some(
              (areaAssessment) => areaAssessment.areaId === area.id,
            ),
        )
        .map((area) => ({
          id: area.id,
          name: area.name,
          buildingName: building.name,
        })),
    );

  if (selected.length === 0) return null;

  const update = (next: GfsAreaAssessment) => {
    onChange(
      [
        ...areaAssessments.filter((current) => current.areaId !== next.areaId),
        next,
      ],
      next.areaId,
    );
  };

  return (
    <section className="space-y-4">
      {selected.map((area) => (
        <AreaAssessmentCard
          area={area}
          value={
            areaAssessments.find((current) => current.areaId === area.id) ??
            emptyAreaAssessment(area.id)
          }
          onChange={update}
          persistenceState={
            persistenceStates[area.id] ?? PersistenceState.SAVED
          }
          catalogItems={catalogItems}
          catalogConsumables={catalogConsumables}
          onMonitorSelected={onMonitorSelected}
          onRemove={() => onRemove(area.id)}
          key={area.id}
        />
      ))}
    </section>
  );
}

function AreaAssessmentCard({
  area,
  value,
  onChange,
  persistenceState,
  catalogItems,
  catalogConsumables,
  onMonitorSelected,
  onRemove,
}: {
  area: { id: string; name: string; buildingName: string };
  value: GfsAreaAssessment;
  onChange: (next: GfsAreaAssessment) => void;
  persistenceState: PersistenceState;
  catalogItems: GfsCatalogItem[];
  catalogConsumables: GfsConsumable[];
  onMonitorSelected?: (item: GfsCatalogItem) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(true);
  const update = (changes: Partial<GfsAreaAssessment>) =>
    onChange({ ...value, ...changes });
  const addMonitor = (catalogItemId: string) => {
    const item = catalogItems.find(
      (candidate) => candidate.id === catalogItemId,
    );
    if (!item) return;
    onMonitorSelected?.(item);
    const existing = value.monitors.find(
      (monitor) => monitor.catalogItemId === item.id,
    );
    const monitors = existing
      ? value.monitors.map((monitor) =>
          monitor.catalogItemId === item.id
            ? { ...monitor, amount: monitor.amount + 1 }
            : monitor,
        )
      : [
          ...value.monitors,
          {
            catalogItemId: item.id,
            name: item.monitor.name,
            type: item.monitor.type,
            amount: 1,
          },
        ];
    update({ monitors });
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="overflow-x-auto border">
        <div className="bg-muted/50 flex flex-col justify-between md:flex-row">
          <CollapsibleTrigger className="flex items-center text-left text-sm font-bold uppercase">
            <span className="py-3 pl-4">
              {open ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </span>
            <span className="py-3 pr-5 pl-3">
              {area.buildingName} - {area.name}
            </span>
          </CollapsibleTrigger>
          <div className="flex items-center gap-2 px-4 py-2">
            <DialogTooltipButton
              tooltip={t("Bereich entfernen")}
              dialogTitle={t("Bereich wirklich entfernen?")}
              icon={<Trash2 className="h-4 w-4" />}
              buttonProps={{
                "aria-label": t("Bereich entfernen"),
                type: "button",
                variant: "ghost",
                size: "icon",
              }}
              content={
                <>
                  <p className="text-muted-foreground text-sm">
                    {t(
                      "Beim Entfernen werden alle Parameter und ausgewählten Materialien dieses Bereichs entfernt. Der Bereich bleibt in den Gebäudedaten erhalten.",
                    )}
                  </p>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button type="button" variant="outline">
                        {t("Abbrechen")}
                      </Button>
                    </DialogClose>
                    <DialogClose asChild>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={onRemove}
                      >
                        {t("Bereich entfernen")}
                      </Button>
                    </DialogClose>
                  </DialogFooter>
                </>
              }
            />
            <PersistenceStateInfo
              state={persistenceState}
              className="shrink-0"
            />
          </div>
        </div>
        <CollapsibleContent>
          <div className="space-y-4 px-4 py-3">
            <div className="grid gap-3 md:grid-cols-3">
              <AssessmentField label={t("Arbeitszeit")}>
                <TimeNumberInput
                  value={value.laborDuration ?? 0}
                  onChange={(laborDuration) => update({ laborDuration })}
                />
              </AssessmentField>
              <AssessmentField label={t("Fläche in m²")}>
                <FloatNumberInput
                  value={value.areaSizeM2 ?? 0}
                  onChange={(areaSizeM2) => update({ areaSizeM2 })}
                />
              </AssessmentField>
              <AssessmentField label={t("Befallsstärke")}>
                <Select
                  value={value.infestationLevel || "none"}
                  onValueChange={(level) =>
                    update({
                      infestationLevel:
                        level === "none"
                          ? ""
                          : (level as GfsAssessment["infestationLevel"]),
                    })
                  }
                >
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("Nicht angegeben")}</SelectItem>
                    <SelectItem value="light">{t("Leicht")}</SelectItem>
                    <SelectItem value="medium">{t("Mittel")}</SelectItem>
                    <SelectItem value="heavy">{t("Stark")}</SelectItem>
                  </SelectContent>
                </Select>
              </AssessmentField>
            </div>
            <AssessmentField
              label={`${t("Bemerkungen für Bereich")} ${area.name}`}
            >
              <TextareaDebounced
                className="min-h-10"
                value={value.finding ?? ""}
                onChange={(event) => update({ finding: event.target.value })}
              />
            </AssessmentField>
          </div>
          <AreaAssessmentSection title={t("Monitore")}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>POS</TableHead>
                  <TableHead>{t("Monitor")}</TableHead>
                  <TableHead>{t("Typ")}</TableHead>
                  <TableHead className="text-right">{t("Anzahl")}</TableHead>
                  <TableHead className="w-2/5">{t("Bemerkung")}</TableHead>
                  <TableHead className="w-fit text-right">
                    {t("Aktion")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {value.monitors.map((monitor, index) => (
                  <TableRow key={monitor.catalogItemId}>
                    <TableCell className="py-1 font-bold">
                      {index + 1}
                    </TableCell>
                    <TableCell className="py-1">{monitor.name}</TableCell>
                    <TableCell className="py-1">
                      {monitorTypeLabel(monitor.type)}
                    </TableCell>
                    <TableCell className="py-1 text-right">
                      <div className="flex justify-end">
                        <Label
                          htmlFor={`${monitor.catalogItemId}-amount`}
                          className="sr-only"
                        >
                          {monitor.name} {t("Anzahl")}
                        </Label>
                        <IntNumberInput
                          id={`${monitor.catalogItemId}-amount`}
                          min={1}
                          value={monitor.amount}
                          onChange={(amount) =>
                            update({
                              monitors: value.monitors.map((current) =>
                                current.catalogItemId === monitor.catalogItemId
                                  ? {
                                      ...current,
                                      amount: Math.max(1, amount || 1),
                                    }
                                  : current,
                              ),
                            })
                          }
                        />
                      </div>
                    </TableCell>
                    <TableCell className="py-1">
                      <TextareaDebounced
                        className="min-h-8 w-full p-1"
                        placeholder={t("Bemerkung")}
                        value={monitor.comment ?? ""}
                        onChange={(event) =>
                          update({
                            monitors: value.monitors.map((current) =>
                              current.catalogItemId === monitor.catalogItemId
                                ? { ...current, comment: event.target.value }
                                : current,
                            ),
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="py-1 text-right">
                      <Button
                        aria-label={`${monitor.name} ${t("Entfernen")}`}
                        onClick={() =>
                          update({
                            monitors: value.monitors.filter(
                              (current) =>
                                current.catalogItemId !== monitor.catalogItemId,
                            ),
                          })
                        }
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex pt-1">
              <MonitorSelectSearchComponent
                monitorList={toQpmMonitorSnaps(catalogItems)}
                onMonitorSelect={addMonitor}
              />
            </div>
          </AreaAssessmentSection>
          <AreaAssessmentConsumables
            consumables={catalogConsumables}
            materials={value.materials}
            onChange={(materials) => update({ materials })}
          />
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function AreaAssessmentConsumables({
  consumables,
  materials,
  onChange,
}: {
  consumables: GfsConsumable[];
  materials: GfsAreaAssessment["materials"];
  onChange: (materials: GfsAreaAssessment["materials"]) => void;
}) {
  const update = (
    index: number,
    patch: Partial<GfsAreaAssessment["materials"][number]>,
  ) =>
    onChange(
      materials.map((material, currentIndex) =>
        currentIndex === index ? { ...material, ...patch } : material,
      ),
    );

  const add = (consumable: GfsConsumable) => {
    const existingIndex = materials.findIndex(
      (material) => material.sourceId === consumable.id,
    );
    if (existingIndex >= 0) {
      update(existingIndex, {
        quantity: materials[existingIndex].quantity + 1,
      });
      return;
    }

    onChange([
      ...materials,
      {
        sourceId: consumable.id,
        key: consumable.id,
        name: consumable.name,
        unit: consumable.unit,
        quantity: 1,
        unitPrice: consumable.unitPrice,
        productDisplayText: consumable.productDisplayText,
        takenAt: new Date().toISOString(),
      },
    ]);
  };

  return (
    <AreaAssessmentSection title={t("Verbrauchsmaterialien")}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>POS</TableHead>
            <TableHead>{t("Verbrauchsmaterial")}</TableHead>
            <TableHead>{t("Einheit")}</TableHead>
            <TableHead className="text-right">{t("Menge")}</TableHead>
            <TableHead className="text-right">
              {t("Preis je Einheit")}
            </TableHead>
            <TableHead className="w-2/5">{t("Bemerkung")}</TableHead>
            <TableHead className="w-fit text-right">{t("Aktion")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {materials.map((material, index) => (
            <TableRow key={`${material.sourceId ?? material.key}-${index}`}>
              <TableCell className="py-1 font-bold">{index + 1}</TableCell>
              <TableCell className="py-1">
                <span title={formatGfsConsumableDisplayName(material)}>
                  {formatGfsConsumableDisplayName(material)}
                </span>
              </TableCell>
              <TableCell className="py-1">
                <Select
                  value={normalizeGfsUnit(material.unit)}
                  onValueChange={(unit) =>
                    update(index, { unit: normalizeGfsUnit(unit) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GfsPriceUnitOptions.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {t(unit.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="py-1">
                <InputDebounced
                  aria-label={`${formatGfsConsumableDisplayName(material)} ${t("Menge")}`}
                  className="text-right"
                  min="0"
                  step="any"
                  type="number"
                  value={material.quantity}
                  onChange={(event) =>
                    update(index, { quantity: Number(event.target.value) })
                  }
                />
              </TableCell>
              <TableCell className="py-1">
                <InputDebounced
                  aria-label={`${formatGfsConsumableDisplayName(material)} ${t("Preis je Einheit")}`}
                  className="text-right"
                  min="0"
                  step="0.01"
                  type="number"
                  value={material.unitPrice}
                  onChange={(event) =>
                    update(index, { unitPrice: Number(event.target.value) })
                  }
                />
              </TableCell>
              <TableCell className="py-1">
                <TextareaDebounced
                  aria-label={`${formatGfsConsumableDisplayName(material)} ${t("Bemerkung")}`}
                  className="min-h-8 w-full p-1"
                  placeholder={t("Bemerkung")}
                  value={material.comment ?? ""}
                  onChange={(event) =>
                    update(index, { comment: event.target.value })
                  }
                />
              </TableCell>
              <TableCell className="py-1 text-right">
                <Button
                  aria-label={`${formatGfsConsumableDisplayName(material)} ${t("Entfernen")}`}
                  onClick={() =>
                    onChange(
                      materials.filter(
                        (_material, currentIndex) => currentIndex !== index,
                      ),
                    )
                  }
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {materials.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {t("Keine Verbrauchsmaterialien")}
        </p>
      )}
      <div className="flex pt-1">
        <ConsumableSelectSearchComponent
          consumables={consumables}
          onConsumableSelect={add}
        />
      </div>
    </AreaAssessmentSection>
  );
}

function AreaAssessmentSection({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) {
  return (
    <section className="space-y-3 border-t px-4 py-4">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function ConsumableSelectSearchComponent({
  consumables,
  onConsumableSelect,
}: {
  consumables: GfsConsumable[];
  onConsumableSelect?: (consumable: GfsConsumable) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-75 justify-between"
        >
          {t("Verbrauchsmaterial auswählen")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-75 p-0">
        <Command className="max-h-72">
          <CommandInput
            placeholder={t("Verbrauchsmaterial suchen...")}
            className="h-9"
          />
          <ScrollArea className="w-full overflow-auto overscroll-y-contain rounded-md border">
            <CommandGroup>
              {consumables.map((consumable) => (
                <CommandItem
                  key={consumable.id}
                  value={formatGfsConsumableDisplayName(consumable)}
                  onSelect={() => {
                    setOpen(false);
                    onConsumableSelect?.(consumable);
                  }}
                >
                  {formatGfsConsumableDisplayName(consumable)}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandEmpty>
              {t("Verbrauchsmaterial nicht vorhanden.")}
            </CommandEmpty>
          </ScrollArea>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function emptyAreaAssessment(areaId: string): GfsAreaAssessment {
  return {
    areaId,
    pestCategories: [],
    infestationLevel: "",
    laborDuration: 0,
    areaSizeM2: 0,
    finding: "",
    measures: [],
    monitors: [],
    materials: [],
  };
}

function toQpmMonitorSnaps(catalogItems: GfsCatalogItem[]): MonitorSnapDTO[] {
  return catalogItems.map((item) => ({
    sourceId: item.id,
    name: item.monitor.name,
    supplier: item.monitor.supplier,
    type: toQpmMonitorType(item.monitor.type),
    times: item.monitor.times,
    capabilities: {
      permanentBaiting: item.monitor.capabilities.permanentBaiting,
      hasLethalAgent: item.monitor.capabilities.hasLethalAgent,
      rentable: false,
      rentalOnly: false,
      checkpointVisual: item.monitor.capabilities.checkpointVisual,
      includeInCheckpointService: false,
    },
    recommendedVisits: item.monitor.recommendedVisits,
    details: {
      description: item.monitor.details.description,
      installationType: item.monitor.details.installationType,
      heightMM: item.monitor.details.heightMm,
      widthMM: item.monitor.details.widthMm,
      depthMM: item.monitor.details.depthMm,
      weightG: item.monitor.details.weightG,
      coverageValue: item.monitor.details.coverageValue,
      coveragePreText: item.monitor.details.coveragePreText,
      coveragePostText: item.monitor.details.coveragePostText,
      starterSpecification: item.monitor.details.starterSpecification,
      protectionLevel: item.monitor.details.protectionLevel,
    },
    pests: [],
    additionals: [],
    consumables: [],
    sourceVersion: 1,
    rentingDefaults: {},
    takenAt: item.monitor.takenAt,
  }));
}

function monitorTypeLabel(type: string): string {
  if (Object.values(MonitorType).includes(type as MonitorType)) {
    return MonitorTypeUtil.toString(type as MonitorType);
  }
  return type;
}

function toQpmMonitorType(type: PestcontrolMonitorType): MonitorType {
  switch (type) {
    case PestcontrolMonitorType.Checkpoint:
      return MonitorType.Checkpoint;
    case PestcontrolMonitorType.FlyExterminator:
      return MonitorType.FlyExterminator;
    case PestcontrolMonitorType.IoT:
      return MonitorType.IoT;
  }
}
